/**
 * Ganchos da aba "Quem clicou" (C1–C4). Ficam fora de `lib/api.ts` para que a aba seja uma peça
 * inteira — tipos, consultas e a ação auditada de revelar o telefone num lugar só.
 */
import { useMutation, useQuery } from '@tanstack/react-query';

import { useTRPC, useTRPCClient } from '../../lib/api';

type Client = ReturnType<typeof useTRPCClient>;
type Out<T extends { query: (...a: never[]) => unknown }> = Awaited<ReturnType<T['query']>>;

export type WhoClickedPage = Out<Client['redirector']['whoClicked']>;
export type WhoClickedRow = WhoClickedPage['rows'][number];
export type WhoClickedSummary = WhoClickedPage['summary'];
export type DeviceSummary = Out<Client['redirector']['deviceSummary']>;
export type RegionSummary = Out<Client['redirector']['regionSummary']>;

export type Janela = { from: Date; to: Date };

export function useWhoClicked(
  workspaceId: string,
  redirectorId: string | null,
  janela: Janela,
  limit: number,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.whoClicked.queryOptions({
      workspaceId,
      redirectorId: redirectorId ?? '',
      from: janela.from,
      to: janela.to,
      limit,
    }),
    enabled: redirectorId !== null,
    // Ao pedir mais linhas, a lista que já está na tela fica até a nova chegar.
    placeholderData: (anterior) => anterior,
  });
}

export function useDeviceSummary(workspaceId: string, redirectorId: string | null, janela: Janela) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.deviceSummary.queryOptions({
      workspaceId,
      redirectorId: redirectorId ?? '',
      from: janela.from,
      to: janela.to,
    }),
    enabled: redirectorId !== null,
  });
}

export function useRegionSummary(workspaceId: string, redirectorId: string | null, janela: Janela) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.regionSummary.queryOptions({
      workspaceId,
      redirectorId: redirectorId ?? '',
      from: janela.from,
      to: janela.to,
    }),
    enabled: redirectorId !== null,
  });
}

/**
 * Revelar o telefone inteiro. O servidor cobra `pii:reveal` e grava a auditoria; esconder o
 * botão por papel na tela é só conforto.
 */
export function useRevealClickPhone() {
  const trpc = useTRPC();
  return useMutation(trpc.redirector.revealClickPhone.mutationOptions());
}

/** A permissão que o servidor cobra para revelar. Mantida aqui para combinar com ele. */
export const REVEAL_PHONE_PERMISSION = 'pii:reveal' as const;
